import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { useAssetId } from "@/setup/hooks/useAssetId";
import { LocationSelector } from "@/shared/selectors";
import { pageBgColor } from "@/utils/theme";
import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import { Grid2, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { GridApi } from "ag-grid-community";
import { Moment } from "moment-timezone";
import { useEffect, useState } from "react";
import { AdjustmentReasonSelector } from "../../../shared/selectors/AdjustmentReasonSelector";
import { AdjustmentTypeSelector } from "../../../shared/selectors/AdjustmentTypeSelector";
import { OperatorSelector } from "../../../shared/selectors/OperatorSelector";
import {
	EndFilter,
	StartFilter,
} from "../../delays/shared/components/StartEndFilter";
import { AdjustmentsReportTable } from "./components/AdjustmentsReportTable";
import { MillAdjustment } from "./models/MillAdjustment";
import { getMillAdjustments } from "./repositories/AdjustmentsReportRepository";

const moment = getMomentTz();

type useAdjustmentsProps = {
	startDate: Date;
	endDate: Date;
	standId?: number | null;
	operatorId?: number | null;
	adjustmentTypeId?: number | null;
	adjustmentReasonId?: number | null;
	onError: (error: Error) => void;
};

export const useAdjustments = ({
	startDate,
	endDate,
	standId,
	operatorId,
	adjustmentTypeId,
	adjustmentReasonId,
	onError,
}: useAdjustmentsProps) => {
	return useQuery<MillAdjustment[], Error>({
		queryKey: [
			"adjustments",
			startDate,
			endDate,
			standId,
			operatorId,
			adjustmentTypeId,
			adjustmentReasonId,
		],
		queryFn: ({ signal }) =>
			getMillAdjustments(
				{
					startDate,
					endDate,
					standId: standId || undefined,
					operatorId,
					adjustmentTypeId,
					adjustmentReasonId,
				},
				signal,
			),
		onError: onError,
		enabled: true,
	});
};

export const AdjustmentsReportPage = () => {
	const assetId = useAssetId();
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const [startDate, setStartDate] = useState<Moment>(
		moment().subtract(7, "days").startOf("day"),
	);
	const [endDate, setEndDate] = useState<Moment>(moment().endOf("day"));
	const [standId, setStandId] = useState<number | null>(null);
	const [operatorId, setOperatorId] = useState<number | null>(null);
	const [adjustmentTypeId, setAdjustmentTypeId] = useState<number | null>(null);
	const [adjustmentReasonId, setAdjustmentReasonId] = useState<number | null>(
		null,
	);

	const { data: rows, isLoading } = useAdjustments({
		startDate: startDate.toDate(),
		endDate: endDate.toDate(),
		standId,
		operatorId,
		adjustmentTypeId,
		adjustmentReasonId,
		onError: (e) => setError(e.message),
	});

	const syncUrlParams = () => {
		const params = new URLSearchParams();
		params.set("start", startDate.format("YYYYMMDD"));
		params.set("end", endDate.format("YYYYMMDD"));
		if (standId) params.set("stands", standId.toString());
		if (operatorId) params.set("operator", operatorId.toString());
		if (adjustmentTypeId)
			params.set("adjustmentType", adjustmentTypeId.toString());
		if (adjustmentReasonId)
			params.set("adjustmentReason", adjustmentReasonId.toString());

		window.history.replaceState(
			{},
			"",
			`${window.location.pathname}?${params.toString()}`,
		);
	};

	const loadUrlParams = () => {
		const params = new URLSearchParams(window.location.search);

		const startParam = params.get("start");
		const endParam = params.get("end");
		const standsParam = params.get("stands");
		const operatorParam = params.get("operator");
		const adjustmentTypeParam = params.get("adjustmentType");
		const adjustmentReasonParam = params.get("adjustmentReason");

		if (startParam) setStartDate(moment(startParam, "YYYYMMDD").startOf("day"));
		if (endParam) setEndDate(moment(endParam, "YYYYMMDD").endOf("day"));
		if (standsParam) setStandId(parseInt(standsParam));
		if (operatorParam) setOperatorId(parseInt(operatorParam));
		if (adjustmentTypeParam) setAdjustmentTypeId(parseInt(adjustmentTypeParam));
		if (adjustmentReasonParam)
			setAdjustmentReasonId(parseInt(adjustmentReasonParam));
	};

	useEffect(() => {
		loadUrlParams();
	}, []);

	const handleStartDateChange = (date: Moment | null) => {
		if (date) setStartDate(date);
	};

	const handleEndDateChange = (date: Moment | null) => {
		if (date) setEndDate(date);
	};

	const handleGridReady = (api: GridApi) => {
		setGridApi(api);
	};

	const handleExportToExcel = () => {
		if (gridApi) {
			const startDateStr = startDate.format("YYYYMMDD");
			const endDateStr = endDate.format("YYYYMMDD");
			const params = {
				fileName: `MillAdjustment_Report_${startDateStr}_${endDateStr}.xlsx`,
				sheetName: `MillAdjustment_Report_${startDateStr}_${endDateStr}`,
			};
			gridApi.exportDataAsExcel(params);
		}
	};

	return (
		<>
			<HelmetDexteel title="Adjustments Report" />

			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{ backgroundColor: pageBgColor, minHeight: "calc(100vh - 60px)" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Adjustments Report
					</Typography>
				</Grid2>

				<Grid2 container spacing={1} size={{ xs: 12 }} alignItems="center">
					<Grid2 style={{ width: "fit-content" }}>
						<StartFilter value={startDate} onChange={handleStartDateChange} />
					</Grid2>
					<Grid2 style={{ width: "fit-content" }}>
						<EndFilter value={endDate} onChange={handleEndDateChange} />
					</Grid2>
					<Grid2 style={{ width: "fit-content" }}>
						<LocationSelector
							label="Stand"
							value={standId}
							onChange={(locationId: number) => setStandId(locationId)}
							onlyStands
						/>
					</Grid2>
					<Grid2 size={{ xs: 2 }}>
						<OperatorSelector
							assetId={assetId}
							label="Operator"
							value={operatorId}
							onChange={setOperatorId}
						/>
					</Grid2>
					<Grid2 size={{ xs: 2 }}>
						<AdjustmentTypeSelector
							label="Adjustment Type"
							value={adjustmentTypeId}
							onChange={(id, requiredValue, requiredReason, unit) =>
								setAdjustmentTypeId(id)
							}
							isFiltering
						/>
					</Grid2>
					<Grid2 size={{ xs: 2 }}>
						<AdjustmentReasonSelector
							label="Adjustment Reason"
							value={adjustmentReasonId}
							onChange={setAdjustmentReasonId}
						/>
					</Grid2>
					<Grid2 size={{ xs: 1 }}>
						<ExcelButton
							handleExportToExcel={handleExportToExcel}
							disabled={!rows || rows.length === 0}
						/>
					</Grid2>
				</Grid2>

				<Grid2>
					<AdjustmentsReportTable
						rows={rows || []}
						loading={isLoading}
						onGridReady={handleGridReady}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
